import { parseHtmlString } from '@bts-web/utils-functions';
import React from 'react';

/**
 * HTMLRenderer Component
 *
 * This React component takes a string input and renders it as formatted HTML.
 * It supports simple custom markdown-style formatting:
 * - Triple asterisks (***text***) or double asterisks (**text**) are rendered as bold text.
 * - Newline characters (`\n` or actual newlines) are converted to line breaks (<br />).
 * - Lines starting with `- ` are grouped into an unordered list (<ul>/<li>).
 * - Lines starting with `1. `, `2. `, etc. are grouped into an ordered list (<ol>/<li>).
 * - HTML entities like &amp; or &#x2f; are decoded before rendering.
 *
 * Example:
 *   Input: "Hello ***World***\nNew line"
 *   Output: Renders "Hello " + <span data-element="markdown-style">World</span> + <br /> + "New line"
 *
 * Props:
 * - children (string): The raw text content to be formatted and rendered.
 */

type TextBlock = { type: 'text'; content: string };

type ListBlock = { type: 'ul' | 'ol'; items: string[] };

type Block = TextBlock | ListBlock;

const renderSegments = (text: string) => {
  const segments = text.split(/(\*\*\*.*?\*\*\*|\*\*.*?\*\*)/);

  return segments.map((segment, index) => {
    const tripleAsterisk = segment.startsWith('***') && segment.endsWith('***');

    const doubleAsterisk = segment.startsWith('**') && segment.endsWith('**');

    const boldContent = tripleAsterisk
      ? segment.slice(3, -3)
      : doubleAsterisk
        ? segment.slice(2, -2)
        : null;

    if (boldContent !== null) {
      return (
        <span data-element="markdown-style" key={index + 'text-fragment'}>
          {boldContent}
        </span>
      );
    }

    return <span key={index + 'text-fragment'}>{segment}</span>;
  });
};

const groupIntoBlocks = (lines: string[]): Block[] => {
  const blocks: Block[] = [];

  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (/^- /.test(line)) {
      const items: string[] = [];

      while (i < lines.length && /^- /.test(lines[i])) {
        items.push(lines[i].slice(2));

        i++;
      }

      blocks.push({ type: 'ul', items });
    } else if (/^\d+\. /.test(line)) {
      const items: string[] = [];

      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ''));

        i++;
      }

      blocks.push({ type: 'ol', items });
    } else {
      blocks.push({ type: 'text', content: line });

      i++;
    }
  }

  return blocks;
};

export const HTMLRenderer = ({ children }: { children: string }) => {
  const renderFormattedText = (input: string) => {
    const decoded = parseHtmlString(input) || input; // Decode known HTML entities

    const lines = decoded.trim().split(/\\n|\n/);

    const blocks = groupIntoBlocks(lines);

    return blocks.map((block, blockIndex) => {
      if (block.type !== 'text') {
        const Tag = block.type;

        return (
          <Tag key={`list-${blockIndex}`}>
            {block.items.map((item, itemIndex) => (
              <li key={`item-${blockIndex}-${itemIndex}`}>
                {renderSegments(item)}
              </li>
            ))}
          </Tag>
        );
      } else {
        const prevBlock = blockIndex > 0 ? blocks[blockIndex - 1] : null;

        const needsBr = prevBlock !== null && prevBlock.type === 'text';

        return (
          <React.Fragment key={`line-${blockIndex}`}>
            {needsBr && <br />}
            {renderSegments(block.content)}
          </React.Fragment>
        );
      }
    });
  };

  return <>{renderFormattedText(children)}</>;
};
